import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { Loader2, Download, ArrowLeft } from "lucide-react";
import { cadDownloadSlug } from "@/lib/cadFilename";

/**
 * Standalone fullscreen 3D viewer — opens from CadDialog's
 * "Open in new tab" link so designers can show clients the mesh
 * without the rest of the CAD workshop UI cluttering the screen.
 *
 * Path: /3d-viewer/:jobId
 */
export default function MeshViewer() {
  const { jobId } = useParams();
  const [meshUrl, setMeshUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewerReady, setViewerReady] = useState(false);

  // Lazy-load model-viewer web component
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await import("@google/model-viewer");
        if (typeof window !== "undefined" && "customElements" in window) {
          await customElements.whenDefined("model-viewer");
        }
        if (!cancelled) setViewerReady(true);
      } catch {
        if (!cancelled) setViewerReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchMesh = useCallback(async () => {
    setError(null);
    setProgress(0);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/studio/cad/tripo/jobs/${jobId}/download?inline=1`,
        {
          responseType: "blob",
          timeout: 90000,
          onDownloadProgress: (evt) => {
            if (evt.total) setProgress(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
            else if (evt.loaded) setProgress(-Math.round(evt.loaded / 1024 / 1024));
          },
        },
      );
      setMeshUrl(URL.createObjectURL(res.data));
      setProgress(100);
    } catch (err) {
      const status = err?.response?.status;
      const reason =
        status === 401 ? "You're signed out — open Facetra Studio, sign in, then refresh this tab."
        : status === 404 ? "Mesh file not found."
        : status === 409 ? "Mesh isn't ready yet."
        : err?.code === "ECONNABORTED" ? "Download timed out."
        : err?.response?.data?.detail || err?.message || "Could not load the mesh.";
      setError(String(reason));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) fetchMesh();
    return () => { if (meshUrl) URL.revokeObjectURL(meshUrl); };
  }, [jobId, fetchMesh]);

  const downloadFile = () => {
    if (!meshUrl) return;
    const a = document.createElement("a");
    a.href = meshUrl;
    a.download = `facetra_3d_${cadDownloadSlug(jobId)}.glb`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="fixed inset-0 bg-[#080808] text-white flex flex-col" data-testid="mesh-viewer-fullscreen">
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/8 bg-black/60 backdrop-blur shrink-0">
        <button
          type="button"
          onClick={() => window.close()}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Close
        </button>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80">
          Facetra · 3D viewer
        </div>
        <button
          type="button"
          onClick={downloadFile}
          disabled={!meshUrl}
          data-testid="mesh-viewer-download"
          className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37]/25 transition disabled:opacity-40"
        >
          <Download className="w-3.5 h-3.5" /> .glb
        </button>
      </header>

      <main className="flex-1 relative overflow-hidden">
        {meshUrl && viewerReady && (
          <model-viewer
            src={meshUrl}
            alt="Generated 3D mesh"
            camera-controls
            auto-rotate
            auto-rotate-delay="500"
            rotation-per-second="20deg"
            exposure="0.9"
            shadow-intensity="0.6"
            environment-image="neutral"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", backgroundColor: "transparent" }}
          />
        )}
        {(loading || !viewerReady) && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
            <div className="text-[11px] uppercase tracking-[0.22em]">
              {loading
                ? (progress > 0
                    ? `Loading mesh · ${progress}%`
                    : progress < 0
                      ? `Loading mesh · ${-progress} MB`
                      : "Loading mesh…")
                : "Starting 3D engine…"}
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="max-w-md text-center space-y-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-rose-300/85">Preview unavailable</div>
              <div className="text-sm text-white/80">{error}</div>
              <button
                type="button"
                onClick={fetchMesh}
                className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37]/30 transition"
              >
                <Loader2 className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Retry
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
